import React, { useState, useEffect, useMemo } from 'react';
import { X } from 'lucide-react';
import { DSPreview } from '../development/DSComposerPage';
import snapshotToRenderProps from '../development/pdf/dsSnapshotToBoxes';
import groupBoxesBySheet from '../development/pdf/dsGroupBySheet';
import { dsPackageApi } from '../../services/api';
import { formatTimestamp } from '../../utils/dateTime';

/**
 * Preview one record's frozen datasheet, with the review actions inline — an approver should be
 * able to look at the sheet and act on it without going back to the grid.
 *
 * Renders through the Composer's own DSPreview canvas, fed from the stored snapshot instead of
 * live data. That means a datasheet looks identical whether it is being composed or reviewed,
 * and there is no second look-alike renderer to drift.
 *
 * Nothing here re-resolves anything: if someone edits the underlying values in the Composer
 * after generation this preview does NOT change. That is the freeze doing its job, and it is
 * the regression test for it.
 */
export default function DsPackagePreviewModal({ pkg, record, onClose, onAction, onExport, busy }) {
  const [snapshot, setSnapshot] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    dsPackageApi.snapshot(pkg.id, record.id)
      .then(res => { if (live) { setSnapshot(res.data); setError(''); } })
      .catch(err => { if (live) setError(err?.response?.data?.error || 'Failed to load the snapshot.'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [pkg.id, record.id]);

  // The same mapping the package PDF uses: snapshot lines back into the boxes shape the canvas
  // understands, carrying the frozen values rather than a fresh resolve.
  const canvas = useMemo(() => (snapshot ? snapshotToRenderProps(snapshot) : null), [snapshot]);

  // DSPreview renders a text input for every 'dsvalues' box and writes through setDraftValues.
  // The values here are frozen, so the setter is a no-op: the inputs show the approved text and
  // typing cannot change what was snapshotted.
  const noop = () => {};

  // 720px is too narrow for a two-column ISA sheet. Widened inline rather than adding a
  // stui-modal-xl class — the same thing DSModal already does, so no CSS file change.
  const wide = { maxWidth: 1180, width: '97vw' };

  return (
    <div className="stui-modal-overlay" onClick={onClose}>
      <div className="stui-modal stui-modal-lg" style={wide} onClick={e => e.stopPropagation()}>
        <div className="stui-modal-header">
          <span className="stui-modal-title">
            {/* An Index based package prints one datasheet per index sheet, so that is its name. */}
            {(pkg.dsExecType === 'Index based' && record.indexSheetCode) || record.tagId || `Record ${record.id}`} — {pkg.dsPackage} rev {pkg.dsPackageRev}
          </span>
          <button className="stui-fk-select-btn" onClick={onClose} title="Close"><X size={20} /></button>
        </div>

        <div className="stui-modal-body">
          {error && <p className="stui-hint">{error}</p>}
          {loading && <p className="stui-hint">Loading snapshot…</p>}
          <p className="stui-hint">
            {record.indexSheetCode ? `Index sheet ${record.indexSheetCode} · ` : ''}
            Status {record.dsStatus}
            {record.dsAddedRev && record.dsAddedRev !== 'A' ? ` · added in Rev ${record.dsAddedRev}` : ''}
            {record.revUser ? ` · last changed by ${record.revUser}` : ''}
            {record.revDate ? ` on ${formatTimestamp(record.revDate)}` : ''}
          </p>
          {/* One canvas per sheet, in page order - the same split groupBoxesBySheet gives the
              package PDF, so the preview shows exactly the pages that would print. A datasheet
              frozen before templates could span pages has every box on sheet 1 and renders as
              the single page it has always been. */}
          {canvas && groupBoxesBySheet(canvas.boxes).map(({ sheet, boxes }, i, pages) => (
            <div key={sheet} style={{ marginBottom: i < pages.length - 1 ? 18 : 0 }}>
              {pages.length > 1 && (
                <p className="stui-hint stui-fw-semibold">
                  {i === pages.length - 1 ? 'Application Specific' : `Content ${sheet}`}
                </p>
              )}
              <DSPreview
                boxes={boxes}
                draftValues={canvas.userValues}
                setDraftValues={noop}
                resolveDataValue={(box) => canvas.resolvedValues[box.id] ?? ''}
                sheetCount={pages.length}
                layout={canvas.metadata.sheetLayouts?.[sheet] ?? null}
              />
            </div>
          ))}
        </div>

        <div className="stui-modal-footer">
          <button className="stui-btn stui-btn-secondary" onClick={onClose}>Close</button>
          <button
            className="stui-btn stui-btn-secondary"
            disabled={busy || !onExport}
            onClick={() => onExport?.([record.id], record.tagId || `record ${record.id}`)}
          >
            Export PDF
          </button>
          {/* Always rendered, disabled when they do not apply — the footer must not reshuffle
              as a record changes status. */}
          <button
            className="stui-btn stui-btn-secondary"
            disabled={busy || record.dsStatus !== 'R'}
            onClick={() => onAction?.(record, 'DR')}
          >
            Return
          </button>
          <button
            className="stui-btn stui-btn-primary"
            disabled={busy || record.dsStatus !== 'D'}
            onClick={() => onAction?.(record, 'R')}
          >
            Submit for review
          </button>
          <button
            className="stui-btn stui-btn-primary"
            disabled={busy || record.dsStatus !== 'R'}
            onClick={() => onAction?.(record, 'A')}
          >
            Approve
          </button>
        </div>
      </div>
    </div>
  );
}
